/**
 * Analytics utility for tracking user events
 * Supports Google Analytics 4 and Plausible Analytics
 *
 * Setup:
 * 1. Set VITE_ANALYTICS_PROVIDER in .env ('ga4' or 'plausible')
 * 2. Set VITE_GA4_MEASUREMENT_ID or VITE_PLAUSIBLE_DOMAIN
 * 3. Call initAnalytics() in main.tsx
 */

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
    plausible?: (...args: any[]) => void;
  }
}

type AnalyticsProvider = 'ga4' | 'plausible' | 'none';

interface EventParams {
  [key: string]: string | number | boolean;
}

class Analytics {
  private provider: AnalyticsProvider = 'none';
  private isInitialized = false;

  /**
   * Initialize analytics
   * Call this once in main.tsx
   */
  init() {
    if (this.isInitialized) return;

    const provider = import.meta.env.VITE_ANALYTICS_PROVIDER as AnalyticsProvider;

    if (!provider || provider === 'none') {
      console.log('[Analytics] No analytics provider configured');
      return;
    }

    this.provider = provider;

    if (provider === 'ga4') {
      this.initGA4();
    } else if (provider === 'plausible') {
      this.initPlausible();
    }

    this.isInitialized = true;
  }

  /**
   * Initialize Google Analytics 4
   */
  private initGA4() {
    const measurementId = import.meta.env.VITE_GA4_MEASUREMENT_ID;

    if (!measurementId) {
      console.warn('[Analytics] GA4 enabled but VITE_GA4_MEASUREMENT_ID not set');
      return;
    }

    // Load GA4 script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer!.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', measurementId, {
      send_page_view: true,
      cookie_flags: 'SameSite=None;Secure',
    });

    console.log('[Analytics] Google Analytics 4 initialized:', measurementId);
  }

  /**
   * Initialize Plausible Analytics
   */
  private initPlausible() {
    const domain = import.meta.env.VITE_PLAUSIBLE_DOMAIN || window.location.hostname;

    // Load Plausible script
    const script = document.createElement('script');
    script.defer = true;
    script.setAttribute('data-domain', domain);
    script.src = 'https://plausible.io/js/script.js';
    document.head.appendChild(script);

    console.log('[Analytics] Plausible Analytics initialized:', domain);
  }

  /**
   * Track a custom event
   *
   * @example
   * analytics.event('button_click', { button_name: 'signup', location: 'hero' })
   */
  event(eventName: string, params?: EventParams) {
    if (!this.isInitialized || this.provider === 'none') return;

    if (this.provider === 'ga4' && window.gtag) {
      window.gtag('event', eventName, params);
    } else if (this.provider === 'plausible' && window.plausible) {
      window.plausible(eventName, { props: params });
    }

    console.log('[Analytics] Event:', eventName, params);
  }

  /**
   * Track a page view
   * Automatically called on route changes
   */
  pageView(path: string, title?: string) {
    if (!this.isInitialized || this.provider === 'none') return;

    if (this.provider === 'ga4' && window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: path,
        page_title: title || document.title,
      });
    }

    console.log('[Analytics] Page view:', path);
  }

  /**
   * Track conversion events (signup, purchase, etc.)
   */
  conversion(eventName: string, value?: number, currency: string = 'USD') {
    const params: EventParams = { currency };
    if (value !== undefined) {
      params.value = value;
    }
    this.event(eventName, params);
  }

  /**
   * Track CTA clicks
   */
  ctaClick(ctaName: string, location: string) {
    this.event('cta_click', {
      cta_name: ctaName,
      location,
    });
  }

  /**
   * Track form submissions
   */
  formSubmit(formName: string) {
    this.event('form_submit', {
      form_name: formName,
    });
  }

  /**
   * Track link clicks (outbound, downloads, etc.)
   */
  linkClick(url: string, linkText: string, linkType: 'outbound' | 'download' | 'internal' = 'internal') {
    this.event('link_click', {
      link_url: url,
      link_text: linkText,
      link_type: linkType,
    });
  }
}

// Export singleton instance
export const analytics = new Analytics();

// Helper hook for React components
export function useAnalytics() {
  return {
    trackEvent: analytics.event.bind(analytics),
    trackPageView: analytics.pageView.bind(analytics),
    trackConversion: analytics.conversion.bind(analytics),
    trackCTAClick: analytics.ctaClick.bind(analytics),
    trackFormSubmit: analytics.formSubmit.bind(analytics),
    trackLinkClick: analytics.linkClick.bind(analytics),
  };
}
